import React from "react";
import { keyframes } from "@emotion/react";
import { Box, Stack, Typography } from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import { COLORS } from "./constants";
import { panelSx } from "./styles";
import { platformIcon } from "./utils";
import { StatusBadge } from "./StatusBadge";
import { Mark } from "../Logo";

// ─────────────────────────────────────────────────────────────────────────────
// SignalPath
//
// The whole route on one line: source -> Fennira -> where it goes. It answers
// "is it working, and if not, which link is broken?" before anyone reads a
// number. Every state comes from data the dashboard already polls; nothing is
// inferred beyond what the panels below already show.
//
// SRT is drawn as its own route (camera -> relay -> your player) because the
// relay never feeds the destinations.
// ─────────────────────────────────────────────────────────────────────────────

const flow = keyframes`
  from { left: 0; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  to   { left: calc(100% - 8px); opacity: 0; }
`;

const SOURCE = {
  rtmp:    { name: "OBS · RTMP",   Icon: CameraAltOutlinedIcon },
  srt:     { name: "SRT camera",   Icon: LockOutlinedIcon },
  browser: { name: "This browser", Icon: DesktopWindowsOutlinedIcon },
  file:    { name: "MP4 file",     Icon: MovieOutlinedIcon }
};

function Node({ kicker, title, meta, icon, accent, line, children }) {
  return (
    <Box sx={{
      flex: { xs: "none", md: "0 0 228px" }, minWidth: 0, p: 2, borderRadius: "16px",
      bgcolor: accent ? "rgba(43,189,175,0.10)" : "rgba(239,227,204,0.03)",
      border: `1px solid ${line || (accent ? "rgba(43,189,175,0.40)" : COLORS.border)}`,
      display: "flex", flexDirection: "column", gap: 1.25
    }}>
      <Typography sx={{ color: COLORS.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {kicker}
      </Typography>
      <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
          bgcolor: accent ? COLORS.bg : COLORS.raised, color: COLORS.text,
          display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 19 }
        }}>
          {icon}
        </Box>
        <Typography noWrap sx={{ color: COLORS.text, fontWeight: 600, fontSize: 15.5 }}>{title}</Typography>
      </Stack>
      {meta && (
        <Typography noWrap sx={{ color: COLORS.muted, fontSize: 12, fontFamily: COLORS.fontMono }}>{meta}</Typography>
      )}
      {children}
    </Box>
  );
}

function Wire({ active }) {
  return (
    <Box aria-hidden="true" sx={{
      position: "relative", alignSelf: "center",
      flex: { xs: "none", md: "1 1 40px" },
      width: { xs: 2, md: "auto" }, height: { xs: 24, md: 2 }, minWidth: { md: 40 },
      borderRadius: 2, bgcolor: active ? "rgba(43,189,175,0.45)" : COLORS.borderStrong
    }}>
      {active && [0, 1, 2].map((i) => (
        <Box key={i} sx={{
          display: { xs: "none", md: "block" },
          position: "absolute", top: -3, left: 0, width: 8, height: 8, borderRadius: "50%",
          bgcolor: COLORS.cyan,
          animation: `${flow} 2.2s linear infinite`, animationDelay: `${-i * 0.73}s`,
          "@media (prefers-reduced-motion: reduce)": { display: "none" }
        }} />
      ))}
    </Box>
  );
}

export function SignalPath({
  sourceKind, ingestLive, fileStreamLive, srtLive, srtCameraConnected = false,
  metrics, platforms, streamStatus, liveCount
}) {
  // The path shows what is ACTUALLY flowing, not which Source tab is open:
  // looking at the MP4 tab while OBS is live must not draw "MP4 file · not
  // connected", and opening the SRT tab must not hide a live broadcast.
  // The browser encoder publishes into the same RTMP ingest, so the only hint
  // that it is the browser rather than OBS is the open tab.
  const broadcasting = ingestLive || fileStreamLive;
  const isSrt = sourceKind === "srt" && !broadcasting;
  const shownSource = ingestLive ? (sourceKind === "browser" ? "browser" : "rtmp")
    : fileStreamLive ? "file"
    : sourceKind;
  const src = SOURCE[shownSource] || SOURCE.rtmp;
  // For SRT, "relay running" is not "video arriving": the relay listens
  // with no camera at all. Only a connected camera means video is flowing.
  const sending = isSrt ? (srtLive && srtCameraConnected) : broadcasting;
  const fps = metrics?.fps;
  const speed = metrics?.speed;

  const outActive = isSrt ? sending : liveCount > 0;
  const destState = platforms.length === 0 ? "error"
    : liveCount > 0 ? "live"
    : sending ? "retrying"
    : "ready";

  const note = isSrt
    ? (!srtLive ? "Relay off · start it in the SRT tab"
      : srtCameraConnected ? "Camera connected · watch it in your player"
      : "Relay running · waiting for your camera")
    : !sending ? (shownSource === "file" ? "Waiting for the scheduled MP4" : "Waiting for your encoder")
    : liveCount > 0 ? "Video is flowing end to end"
    : "Video is arriving, no destination is sending yet";

  return (
    <Box component="section" aria-labelledby="signal-path-title" sx={panelSx}>
      <Stack direction="row" flexWrap="wrap" alignItems="baseline" justifyContent="space-between" gap={1} sx={{ mb: 2.5 }}>
        <Typography id="signal-path-title" sx={{
          display: "flex", alignItems: "center", gap: 1,
          color: COLORS.muted, fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
          "&::before": {
            content: '""', width: 0, height: 0,
            borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
            borderBottom: `8px solid ${COLORS.cyan}`
          }
        }}>
          Signal path
        </Typography>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 13 }} role="status">{note}</Typography>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" sx={{ overflowX: "auto" }}>
        <Node
          kicker="Source" title={src.name} icon={<src.Icon />}
          line={sending ? "rgba(43,189,175,0.40)" : undefined}
          meta={isSrt
            ? (!srtLive ? "relay off" : srtCameraConnected ? "camera connected" : "waiting for camera")
            : sending ? (fps != null ? `${fps} fps` : "connected") : "not connected"}
        />
        <Wire active={sending} />
        <Node
          kicker="Relay" title="Fennira" accent
          icon={<Mark size={22} />}
          meta={`studio.fennira.com${sending && speed != null ? ` · ${speed.toFixed(2)}×` : ""}`}
        />
        <Wire active={outActive} />
        {isSrt ? (
          <Node
            kicker="Viewer" title="Your player" icon={<LiveTvOutlinedIcon />}
            meta="VLC, ffplay or vMix"
          >
            <Box><StatusBadge state={srtLive ? "live" : "offline"} label={srtLive ? "Relay on" : "Relay off"} /></Box>
          </Node>
        ) : (
          <Node
            kicker="Destinations"
            title={platforms.length ? `${liveCount} of ${platforms.length} live` : "None yet"}
            line={liveCount > 0 ? "rgba(61,214,140,0.34)" : undefined}
            icon={platforms[0] ? platformIcon(platforms[0].kind, 36) : <LiveTvOutlinedIcon />}
          >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <StatusBadge
                state={destState}
                label={destState === "error" ? "Add one" : destState === "retrying" ? "Waiting" : undefined}
              />
              {/* One small glyph per destination (up to five) so you can see
                  WHICH are sending, not just how many. */}
              {platforms.slice(0, 5).map((p) => (
                <Box key={p.id} title={`${p.label}: ${streamStatus[p.id] === "live" ? "live" : "not sending"}`} sx={{
                  display: "flex", opacity: streamStatus[p.id] === "live" ? 1 : 0.4
                }}>
                  {platformIcon(p.kind, 18)}
                </Box>
              ))}
            </Stack>
          </Node>
        )}
      </Stack>
    </Box>
  );
}
